import {
  CONTENT_CARD_BODY_TEST_ID,
  CONTENT_CARD_HEADER_TEST_ID,
  CONTENT_CARD_TEST_ID,
  CONTENT_CARD_TITLE_TEST_ID,
} from '@ctw/shared/test/ids';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type PropsWithChildren, type ReactNode, forwardRef } from 'react';

export interface ContentCardProps extends PropsWithChildren {
  titleIcon?: IconDefinition;
  title?: string;
  className?: string;
  variant: 'focal';
  fullbleed?: boolean;
  content?: {
    header: ReactNode;
  };
}

export const ContentCard = forwardRef<HTMLElement, ContentCardProps>(
  ({ fullbleed, title, titleIcon, className, content, children }, ref) => (
    <section
      ref={ref}
      data-testid={CONTENT_CARD_TEST_ID}
      className={twx(
        'flex min-h-max flex-col gap-1.5 overflow-hidden rounded-lg border border-border-main bg-background-hover shadow-sm',
        {
          'p-4': !fullbleed,
        },
        className,
      )}
    >
      {title && (
        <div className={tw`flex items-center justify-between`}>
          <div className={tw`flex flex-1 items-center gap-2`}>
            {titleIcon && (
              <div className={tw`flex items-center justify-center`}>
                <FontAwesomeIcon icon={titleIcon} className={tw`h-3.5 w-3.5 text-content-icon`} />
              </div>
            )}
            <div
              data-testid={CONTENT_CARD_TITLE_TEST_ID}
              className={tw`font-medium text-content-subtle text-xs uppercase leading-tight tracking-wide`}
            >
              {title}
            </div>
          </div>
          {content?.header && (
            <div
              data-testid={CONTENT_CARD_HEADER_TEST_ID}
              className={tw`flex flex-1 items-center justify-end gap-2 text-content-subtle`}
            >
              {content.header}
            </div>
          )}
        </div>
      )}
      <div
        data-testid={CONTENT_CARD_BODY_TEST_ID}
        className={tw`text-content-main text-sm leading-tight`}
      >
        {children}
      </div>
    </section>
  ),
);
